import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Cake,
  ChevronLeft,
  ChevronRight,
  FileWarning,
  RefreshCw,
  UserCog,
  UserPlus,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { formatBRL, nomeMes } from "@/lib/utils";

// ============================================================
// R7 — Painel do RH
// ============================================================
// Arquivo reservado pela R1 e preenchido aqui. E a primeira tela de quem
// cuida de gente: quantos somos, quanto o mes custa, o que trava.
//
// Duas fontes com pesos diferentes: turno, conferencia e aniversario vem do
// proprio escalas (respondem mesmo com a clinica fora do ar); cadastro
// fiscal, modalidade, contrato e dinheiro pago vem da clinica, que e a dona.
// Quando o canal com ela falha, o bloco dela sai da tela com um aviso — o
// painel nunca chuta numero no lugar do ERP.
//
// Sem grafico de biblioteca: as barras sao divs. Menos peso, mesma leitura.

function reais(centavos: number | null | undefined): string {
  return formatBRL((centavos ?? 0) / 100);
}

const ROTULO_SETOR: Record<string, string> = {
  clinica: "Clínica",
  agro: "Agro",
  bt: "Banho e tosa",
  sem_setor: "Sem setor",
};

const ROTULO_MODALIDADE: Record<string, string> = {
  clt: "CLT",
  pf_autonomo: "PF autônomo",
  pj: "PJ",
  socio: "Sócio",
  // 25/09/2026: as duas que o ERP aceita desde 16/09 (checklist-colaborador.ts).
  estagio: "Estágio",
  freela: "Freela (eventual)",
  sem_modalidade: "Sem modalidade",
};

const ROTULO_TIPO: Record<string, string> = {
  veterinario: "Veterinários",
  auxiliar: "Auxiliares",
  ambos: "Vet + Auxiliar",
  // 13/10/2026: secretárias e motorista (valor interno `apoio`).
  apoio: "Secretárias / motorista",
  outro: "Outros",
};

const CORES_BARRA = [
  "bg-emerald-500",
  "bg-sky-500",
  "bg-amber-500",
  "bg-violet-500",
  "bg-slate-400",
];

/** Cor da barra pela posicao. Com fallback: o indice do array e checado.*/
function corDaBarra(i: number): string {
  return CORES_BARRA[i % CORES_BARRA.length] ?? "bg-slate-400";
}

/** Barra horizontal simples. Sem lib: div dentro de div. */
function Barra({
  rotulo,
  valor,
  maximo,
  cor,
  sufixo,
}: {
  rotulo: string;
  valor: number;
  maximo: number;
  cor: string;
  sufixo?: string;
}) {
  const pct = maximo > 0 ? Math.round((valor / maximo) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="text-slate-600">{rotulo}</span>
        <span className="font-semibold text-slate-900">
          {valor}
          {sufixo ? <span className="ml-1 text-xs font-normal text-slate-500">{sufixo}</span> : null}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${cor}`} style={{ width: `${Math.max(pct, valor > 0 ? 3 : 0)}%` }} />
      </div>
    </div>
  );
}

/** Número grande com legenda — o bloco que o olho procura primeiro. */
function Numero({
  rotulo,
  valor,
  detalhe,
  destaque,
}: {
  rotulo: string;
  valor: string;
  detalhe?: string;
  destaque?: boolean;
}) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{rotulo}</p>
      <p className={`mt-1 text-xl font-bold ${destaque ? "text-emerald-700" : "text-slate-900"}`}>{valor}</p>
      {detalhe ? <p className="mt-0.5 text-xs text-slate-500">{detalhe}</p> : null}
    </div>
  );
}

export function PainelRH() {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [setor, setSetor] = useState<"clinica" | "agro" | "bt" | "sem_setor" | "">("");

  const q = trpc.painelRh.resumo.useQuery({ ano, mes, setor: setor === "" ? null : setor });
  const d = q.data;
  // 25/09/2026: quem tem login e não tem ficha (resumo do bloco de
  // Colaboradores). Só contagem — não pergunta nada à clínica.
  const acessos = trpc.colaboradores.acessosSemFicha.useQuery({ comSugestao: false });
  const semFicha = acessos.data?.totais.semFicha ?? 0;
  const naCentral = acessos.data?.totais.aguardandoCentral ?? 0;

  function navegar(delta: number) {
    let novo = mes + delta;
    let novoAno = ano;
    if (novo < 1) {
      novo = 12;
      novoAno--;
    }
    if (novo > 12) {
      novo = 1;
      novoAno++;
    }
    setMes(novo);
    setAno(novoAno);
  }

  const maxSetor = d ? Math.max(1, ...Object.values(d.pessoas.porSetor)) : 1;
  const modalidades = d?.pessoas.porModalidade ?? null;
  const maxModalidade = modalidades ? Math.max(1, ...Object.values(modalidades)) : 1;
  const maxTipo = d ? Math.max(1, ...Object.values(d.pessoas.porTipo)) : 1;
  const maxDinheiro = d
    ? Math.max(1, d.dinheiro.previstoCentavos, d.dinheiro.apuradoCentavos, d.dinheiro.pagoCentavos)
    : 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Painel do RH</h1>
          <p className="text-sm text-slate-600">
            Pessoas por setor e modalidade, previsto x apurado x pago e as pendências que travam o mês.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={setor}
            onChange={(e) => setSetor(e.target.value as typeof setor)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700"
          >
            <option value="">Todos os setores</option>
            <option value="clinica">Clínica</option>
            <option value="agro">Agro</option>
            <option value="bt">Banho e tosa</option>
            <option value="sem_setor">Sem setor</option>
          </select>
          <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-1">
            <Button variant="ghost" size="sm" onClick={() => navegar(-1)} aria-label="Mês anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[128px] text-center text-sm font-medium text-slate-700">
              {nomeMes(mes)} de {ano}
            </span>
            <Button variant="ghost" size="sm" onClick={() => navegar(1)} aria-label="Próximo mês">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {q.isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-slate-500">Carregando o mês…</CardContent>
        </Card>
      ) : q.error ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-red-700">{q.error.message}</CardContent>
        </Card>
      ) : !d ? null : (
        <>
          {(semFicha > 0 || naCentral > 0) && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <UserPlus className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">
                  {[
                    semFicha > 0 ? `${semFicha} ${semFicha === 1 ? "acesso sem ficha" : "acessos sem ficha"}` : null,
                    naCentral > 0
                      ? `${naCentral} ${naCentral === 1 ? "pedido aguardando" : "pedidos aguardando"} a central do ERP`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <p className="text-xs text-amber-800">
                  Quem tem login e não tem ficha não aparece em Colaboradores nem na escala.{" "}
                  <Link to="/colaboradores" className="font-medium underline">
                    Resolver em Colaboradores
                  </Link>
                </p>
              </div>
            </div>
          )}

          {!d.clinica.ok && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">
                  Cadastro, contrato e pagamento não vieram da clínica nesta carga.
                </p>
                <p className="text-xs text-amber-800">
                  {d.clinica.erro} Os números de escala, conferência e aniversário abaixo continuam
                  válidos — eles são do próprio RH.
                </p>
              </div>
            </div>
          )}

          {/* ── Pessoas ─────────────────────────────────────────────── */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardContent className="space-y-3 pt-5">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold text-slate-900">Pessoas ativas</h2>
                  <span className="text-2xl font-bold text-slate-900">{d.pessoas.ativosNoFiltro}</span>
                </div>
                <div className="space-y-2">
                  {Object.entries(d.pessoas.porSetor).map(([chave, valor], i) => (
                    <Barra
                      key={chave}
                      rotulo={ROTULO_SETOR[chave] ?? chave}
                      valor={valor}
                      maximo={maxSetor}
                      cor={corDaBarra(i)}
                    />
                  ))}
                </div>
                <p className="text-xs text-slate-500">
                  {d.pessoas.comTurnoNoMes} com turno em {nomeMes(mes).toLowerCase()}.
                  {d.pessoas.ativosClinica !== null
                    ? ` A clínica tem ${d.pessoas.ativosClinica} pessoas ativas no cadastro dela.`
                    : ""}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 pt-5">
                <h2 className="text-sm font-semibold text-slate-900">Por modalidade</h2>
                {modalidades ? (
                  <div className="space-y-2">
                    {Object.entries(modalidades)
                      .filter(([, v]) => v > 0)
                      .map(([chave, valor], i) => (
                        <Barra
                          key={chave}
                          rotulo={ROTULO_MODALIDADE[chave] ?? chave}
                          valor={valor}
                          maximo={maxModalidade}
                          cor={corDaBarra(i)}
                        />
                      ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">
                    A modalidade de contratação mora no cadastro da clínica e não veio nesta carga.
                  </p>
                )}
                <p className="text-xs text-slate-500">
                  A modalidade da clínica é contada no grupo inteiro; o filtro por setor vale para as
                  listas de pendência abaixo.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 pt-5">
                <h2 className="text-sm font-semibold text-slate-900">Por função no escalas</h2>
                <div className="space-y-2">
                  {Object.entries(d.pessoas.porTipo).length === 0 ? (
                    <p className="text-xs text-slate-500">Ninguém ativo neste filtro.</p>
                  ) : (
                    Object.entries(d.pessoas.porTipo).map(([chave, valor], i) => (
                      <Barra
                        key={chave}
                        rotulo={ROTULO_TIPO[chave] ?? chave}
                        valor={valor}
                        maximo={maxTipo}
                        cor={corDaBarra(i)}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ── Dinheiro do mês ─────────────────────────────────────── */}
          <Card>
            <CardContent className="space-y-4 pt-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold text-slate-900">
                  Turnos de {nomeMes(mes).toLowerCase()}: previsto x apurado x pago
                </h2>
                <div className="flex gap-3 text-xs">
                  <Link to="/conferencia" className="text-emerald-700 hover:underline">
                    Conferência
                  </Link>
                  <Link to="/pagamentos" className="text-emerald-700 hover:underline">
                    Pagamentos
                  </Link>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Numero
                  rotulo="Previsto"
                  valor={reais(d.dinheiro.previstoCentavos)}
                  detalhe={`${d.turnos.total} turnos no mês`}
                />
                <Numero
                  rotulo="Apurado"
                  valor={reais(d.dinheiro.apuradoCentavos)}
                  detalhe={`${d.turnos.realizados} realizados, ${d.turnos.semConferencia} sem conferência`}
                />
                <Numero
                  rotulo="Pago"
                  valor={reais(d.dinheiro.pagoCentavos)}
                  detalhe={`${d.fechamentos.pagos} fechamento(s) quitado(s)`}
                  destaque
                />
                <Numero
                  rotulo="Em aberto na clínica"
                  valor={reais(d.dinheiro.emAbertoCentavos)}
                  detalhe={
                    d.dinheiro.naoFechadoCentavos > 0
                      ? `${reais(d.dinheiro.naoFechadoCentavos)} apurados ainda sem fechamento`
                      : "Tudo que foi apurado já virou fechamento"
                  }
                />
              </div>

              <div className="space-y-2">
                <Barra rotulo="Previsto" valor={d.dinheiro.previstoCentavos} maximo={maxDinheiro} cor="bg-slate-300" />
                <Barra rotulo="Apurado" valor={d.dinheiro.apuradoCentavos} maximo={maxDinheiro} cor="bg-sky-500" />
                <Barra rotulo="Pago" valor={d.dinheiro.pagoCentavos} maximo={maxDinheiro} cor="bg-emerald-500" />
              </div>
              <p className="text-xs text-slate-500">
                As barras comparam centavos: a legenda de cada uma é o valor cru, e o número em reais
                está nos cartões acima. Quem paga é a clínica — aqui é só o espelho.
              </p>

              {d.dinheiro.comissoes && Object.keys(d.dinheiro.comissoes).length > 0 && (
                <div className="border-t border-slate-100 pt-3">
                  <p className="mb-2 text-xs uppercase tracking-wide text-slate-500">
                    Comissões da competência (clínica)
                  </p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {Object.entries(d.dinheiro.comissoes).map(([status, v]) => (
                      <span key={status} className="text-slate-700">
                        <span className="capitalize text-slate-500">{status}:</span>{" "}
                        <span className="font-semibold">{reais(v.valorCentavos)}</span>{" "}
                        <span className="text-xs text-slate-500">({v.qtd})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Competências e pendências ───────────────────────────── */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardContent className="space-y-3 pt-5">
                <h2 className="text-sm font-semibold text-slate-900">Competência do mês</h2>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-amber-50 py-2">
                    <p className="text-lg font-bold text-amber-800">{d.competencias.abertas}</p>
                    <p className="text-xs text-amber-800">Abertas</p>
                  </div>
                  <div className="rounded-md bg-sky-50 py-2">
                    <p className="text-lg font-bold text-sky-800">{d.competencias.conferidas}</p>
                    <p className="text-xs text-sky-800">Conferidas</p>
                  </div>
                  <div className="rounded-md bg-emerald-50 py-2">
                    <p className="text-lg font-bold text-emerald-800">{d.fechamentos.pagos}</p>
                    <p className="text-xs text-emerald-800">Pagas</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  {d.fechamentos.total} fechamento(s) na clínica: {d.fechamentos.abertos} em aberto,{" "}
                  {d.fechamentos.parciais} parcial(is)
                  {d.fechamentos.cancelados > 0 ? `, ${d.fechamentos.cancelados} cancelado(s)` : ""}.
                </p>
                <Link to="/conferencia" className="inline-block text-xs text-emerald-700 hover:underline">
                  Abrir a conferência do mês
                </Link>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 pt-5">
                <div className="flex items-center gap-2">
                  <UserCog className="h-4 w-4 text-slate-400" />
                  <h2 className="text-sm font-semibold text-slate-900">Cadastro incompleto</h2>
                  <span className="ml-auto text-lg font-bold text-slate-900">
                    {d.clinica.ok ? d.pendencias.totalCadastro : "—"}
                  </span>
                </div>
                {!d.clinica.ok ? (
                  <p className="text-xs text-slate-500">Depende do cadastro da clínica.</p>
                ) : d.pendencias.cadastro.length === 0 ? (
                  <p className="text-xs text-slate-500">Nenhuma ficha pendente neste filtro.</p>
                ) : (
                  <ul className="space-y-2">
                    {d.pendencias.cadastro.slice(0, 5).map((p) => (
                      <li key={p.profissionalId} className="text-sm">
                        <span className="font-medium text-slate-800">{p.nome}</span>
                        <span className="ml-2 text-xs text-slate-500">
                          {ROTULO_SETOR[p.setor] ?? p.setor}
                        </span>
                        <p className="text-xs text-slate-500">
                          {p.bloqueios > 0 ? (
                            <span className="text-red-700">{p.bloqueios} impedimento(s) · </span>
                          ) : null}
                          {p.total} campo(s) em falta
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
                {d.pendencias.cadastro.length > 5 && (
                  <p className="text-xs text-slate-500">
                    e mais {d.pendencias.cadastro.length - 5} pessoa(s).
                  </p>
                )}
                <Link to="/colaboradores" className="inline-block text-xs text-emerald-700 hover:underline">
                  Abrir Colaboradores
                </Link>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 pt-5">
                <div className="flex items-center gap-2">
                  <FileWarning className="h-4 w-4 text-slate-400" />
                  <h2 className="text-sm font-semibold text-slate-900">Contrato pendente</h2>
                  <span className="ml-auto text-lg font-bold text-slate-900">
                    {d.clinica.ok ? d.pendencias.totalContrato : "—"}
                  </span>
                </div>
                {!d.clinica.ok ? (
                  <p className="text-xs text-slate-500">Depende do cadastro da clínica.</p>
                ) : d.pendencias.contrato.length === 0 ? (
                  <p className="text-xs text-slate-500">Ninguém sem contrato assinado neste filtro.</p>
                ) : (
                  <ul className="space-y-2">
                    {d.pendencias.contrato.slice(0, 5).map((p) => (
                      <li key={p.profissionalId} className="text-sm">
                        <span className="font-medium text-slate-800">{p.nome}</span>
                        <span className="ml-2 text-xs text-slate-500">
                          {ROTULO_MODALIDADE[p.modalidade] ?? p.modalidade}
                        </span>
                        <p className="text-xs text-slate-500">{p.situacao}</p>
                      </li>
                    ))}
                  </ul>
                )}
                {d.pendencias.contrato.length > 5 && (
                  <p className="text-xs text-slate-500">
                    e mais {d.pendencias.contrato.length - 5} pessoa(s).
                  </p>
                )}
                <Link to="/juridico" className="inline-block text-xs text-emerald-700 hover:underline">
                  Abrir Jurídico
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* ── Alertas e aniversários ──────────────────────────────── */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardContent className="space-y-3 pt-5">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-slate-400" />
                  <h2 className="text-sm font-semibold text-slate-900">Alertas de quarentena e piso</h2>
                  <span className="ml-auto text-lg font-bold text-slate-900">
                    {d.clinica.ok ? d.alertas.length : "—"}
                  </span>
                </div>
                {!d.clinica.ok ? (
                  <p className="text-xs text-slate-500">
                    Os alertas nascem do checklist da clínica e não vieram nesta carga.
                  </p>
                ) : d.alertas.length === 0 ? (
                  <p className="text-xs text-slate-500">Nenhum alerta neste filtro.</p>
                ) : (
                  <ul className="space-y-2">
                    {d.alertas.slice(0, 8).map((a, i) => (
                      <li
                        key={`${a.profissionalId}-${a.campo}-${i}`}
                        className={`rounded-md border px-3 py-2 text-sm ${
                          a.bloqueio
                            ? "border-red-200 bg-red-50 text-red-900"
                            : "border-amber-200 bg-amber-50 text-amber-900"
                        }`}
                      >
                        <span className="font-medium">{a.nome}</span>
                        <span className="ml-2 text-xs opacity-80">{a.rotulo}</span>
                        <p className="text-xs opacity-90">{a.motivo}</p>
                      </li>
                    ))}
                  </ul>
                )}
                {d.clinica.ok && d.alertas.length > 8 && (
                  <p className="text-xs text-slate-500">e mais {d.alertas.length - 8} alerta(s).</p>
                )}
                {d.quarentenaMeses !== null && (
                  <p className="text-xs text-slate-500">
                    Quarentena adotada para contratar como PJ quem foi CLT: {d.quarentenaMeses} meses.
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 pt-5">
                <div className="flex items-center gap-2">
                  <Cake className="h-4 w-4 text-slate-400" />
                  <h2 className="text-sm font-semibold text-slate-900">
                    Aniversariantes de {nomeMes(mes).toLowerCase()}
                  </h2>
                  <span className="ml-auto text-lg font-bold text-slate-900">
                    {d.aniversariantes.length}
                  </span>
                </div>
                {d.aniversariantes.length === 0 ? (
                  <p className="text-xs text-slate-500">
                    Ninguém neste mês — ou a data de nascimento ainda não foi preenchida no cadastro
                    do RH.
                  </p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {d.aniversariantes.map((a) => (
                      <li key={a.id} className="flex items-center justify-between py-1.5 text-sm">
                        <span className="text-slate-800">{a.nome}</span>
                        <span className="text-xs text-slate-500">
                          dia {String(a.dia).padStart(2, "0")} · {ROTULO_SETOR[a.setor] ?? a.setor}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
